import { FC, PropsWithChildren } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { NavigationBackdrop } from './components/backdrop';
import { NavigationContextProvider, useNavigation } from './context/navigation-context-provider';

import { PropSyncHelper } from './helpers/prop-sync-helper';
import { NavigationProps } from './types/navigation.types';
import { getLocale } from '../../util/locales/locales';
import { NavigationWrapper } from './navigation-wrapper';
import { MobileNavigation } from './components/mobile-navigation';
import FocusTrap from 'focus-trap-react';

const queryClient = new QueryClient();

export const FocusTrapWrapper = ({ children }) => {
  const {
    state: { showMobileMenu },
  } = useNavigation();
  return (
    <FocusTrap
      focusTrapOptions={{
        allowOutsideClick: true,
        tabbableOptions: {
          displayCheck: 'legacy-full',
        },
      }}
      active={showMobileMenu}
    >
      <div>{children}</div>
    </FocusTrap>
  );
};

export const Navigation: FC<PropsWithChildren<NavigationProps>> = ({
  layout = 'compact',
  mainNavItems,
  metaNavItems,
  name = process.env.NEXT_PUBLIC_NAVIGATION_APP_NAME,
  homeLinkId,
  homeLinkAriaLabel,
  auth,
  locale = getLocale()?.RFC5646,
  shouldHideMainNavOnDesktop = false,
  mobileNavigationAdditionalSubMenuContent,
  children,
  topOffset = 0,
  mobileNavigationAdditionalSubMenuContentItems,
  callbackAppName,
  callbackUrl,
}) => {
  return (
    <NavigationContextProvider
      config={{
        layout,
        mainNavItems,
        metaNavItems,
        name,
        locale,
        shouldHideMainNavOnDesktop,
        homeLinkId,
        homeLinkAriaLabel,
        callbackAppName,
        callbackUrl,
      }}
      auth={auth}
    >
      <PropSyncHelper
        auth={auth}
        config={{
          layout,
          mainNavItems,
          metaNavItems,
          name,
          locale,
          shouldHideMainNavOnDesktop,
          homeLinkId,
          homeLinkAriaLabel,
          callbackAppName,
          callbackUrl,
        }}
      />
      <QueryClientProvider client={queryClient}>
        <NavigationBackdrop />
        <FocusTrapWrapper>
          <NavigationWrapper topOffset={topOffset}>{children}</NavigationWrapper>
          <MobileNavigation
            additionalSubMenuContent={mobileNavigationAdditionalSubMenuContent}
            additionalSubMenuContentItems={mobileNavigationAdditionalSubMenuContentItems}
          />
        </FocusTrapWrapper>
      </QueryClientProvider>
    </NavigationContextProvider>
  );
};
